import React, { useState, useRef, useEffect, useCallback } from 'react';

// ── 상수 ─────────────────────────────────────────────────────────────────────
const SNAP = 10;
const MIN_SC = 0.3, MAX_SC = 4.0;
const VTXR = 7;          // 꼭짓점 핸들 반지름(px)
const CLOSE_D = 20;      // 폴리곤 닫기 감지 거리(px)
const WALL_W = 12;       // 벽 두께(cm) – 시각적 stroke

// ── 유틸 ──────────────────────────────────────────────────────────────────────
const uid = () => 'x' + Date.now().toString(36) + Math.random().toString(36).slice(2,5);
const snapV = v => Math.round(v / SNAP) * SNAP;

// 신발끈 공식 → m²
function polyArea(pts) {
  if (!pts || pts.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return Math.abs(a) / 2 / 10000;
}

// 폴리곤 내부 점 판별 (Ray-casting)
function ptInPoly(px, py, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi))
      inside = !inside;
  }
  return inside;
}

// 두 선분의 교점
function segXseg(ax,ay,bx,by,cx,cy,dx,dy) {
  const r = {x:bx-ax, y:by-ay}, s = {x:dx-cx, y:dy-cy};
  const d = r.x*s.y - r.y*s.x;
  if (Math.abs(d) < 1e-9) return null;
  const t = ((cx-ax)*s.y - (cy-ay)*s.x) / d;
  const u = ((cx-ax)*r.y - (cy-ay)*r.x) / d;
  if (t>=0 && t<=1 && u>=0 && u<=1)
    return { x:ax+t*r.x, y:ay+t*r.y, t, u, edgeT:u };
  return null;
}

// 폴리곤 가장자리에서 가장 가까운 점
function nearestEdgePt(pts, mx, my) {
  let best = null, bestD = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const j = (i+1) % pts.length;
    const ax=pts[i].x, ay=pts[i].y, bx=pts[j].x, by=pts[j].y;
    const dx=bx-ax, dy=by-ay, len2=dx*dx+dy*dy;
    if (len2 < 1) continue;
    let t = ((mx-ax)*dx + (my-ay)*dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const px=ax+t*dx, py=ay+t*dy;
    const d = Math.hypot(mx-px, my-py);
    if (d < bestD) { bestD=d; best={ edgeIdx:i, t, x:snapV(px), y:snapV(py), dist:d }; }
  }
  return best;
}

// 선분으로 폴리곤 분할: edgePt1, edgePt2 = {edgeIdx, x, y}
function splitPolygon(pts, ep1, ep2) {
  let e1=ep1.edgeIdx, e2=ep2.edgeIdx;
  let p1={x:ep1.x,y:ep1.y}, p2={x:ep2.x,y:ep2.y};
  if (e1 > e2) { [e1,e2]=[e2,e1]; [p1,p2]=[p2,p1]; }

  const polyA=[p1];
  for (let i=e1+1; i<=e2; i++) polyA.push({...pts[i]});
  polyA.push(p2);

  const polyB=[p2];
  for (let i=e2+1; i<pts.length; i++) polyB.push({...pts[i]});
  for (let i=0; i<=e1; i++) polyB.push({...pts[i]});
  polyB.push(p1);

  if (polyA.length < 3 || polyB.length < 3) return null;
  return [polyA, polyB];
}

// 직사각형 → 폴리곤 points
function rectPts(x,y,w,h) {
  return [{x,y},{x:x+w,y},{x:x+w,y:y+h},{x,y:y+h}];
}

// 구버전 방({x,y,width,height}) → 새 형식 migration
function migrateRoom(r) {
  if (r.points) return r;
  return {
    id: r.id, name: r.name,
    points: rectPts(r.x||0, r.y||0, r.width||300, r.height||250),
    floorColor: r.color || r.floorColor || '#fdf8f0',
    doors: (r.doors||[]).map(d=>({...d, edgeIdx: {N:0,E:1,S:2,W:3}[d.wall]??0})),
    windows: (r.windows||[]).map(w=>({...w, edgeIdx: {N:0,E:1,S:2,W:3}[w.wall]??0})),
  };
}

// 폴리곤 중심
function polyCenter(pts) {
  const cx = pts.reduce((s,p)=>s+p.x,0)/pts.length;
  const cy = pts.reduce((s,p)=>s+p.y,0)/pts.length;
  return {x:cx,y:cy};
}

// ── 가구 라이브러리 (FLIB) ────────────────────────────────────────────────────
const FLIB = {
  '거실': [
    {type:'sofa',label:'소파',w:200,h:90},{type:'sofa_l',label:'L형 소파',w:230,h:180},
    {type:'armchair',label:'안락의자',w:80,h:80},{type:'coffee_tb',label:'커피테이블',w:110,h:60},
    {type:'tv_stand',label:'TV장',w:160,h:45},{type:'bookshelf',label:'책장',w:90,h:30},
    {type:'side_tb',label:'사이드테이블',w:50,h:50},
  ],
  '침실': [
    {type:'bed_s',label:'싱글침대',w:100,h:200},{type:'bed_d',label:'더블침대',w:140,h:200},
    {type:'bed_q',label:'퀸침대',w:160,h:200},{type:'bed_k',label:'킹침대',w:200,h:200},
    {type:'wardrobe',label:'옷장',w:180,h:60},{type:'dresser',label:'화장대',w:100,h:50},
    {type:'nightstand',label:'협탁',w:50,h:45},
  ],
  '주방': [
    {type:'counter',label:'주방카운터',w:160,h:60},{type:'counter_l',label:'L형카운터',w:240,h:60},
    {type:'sink_k',label:'싱크대',w:80,h:60},{type:'stove',label:'가스레인지',w:60,h:60},
    {type:'refrigerator',label:'냉장고',w:65,h:70},{type:'island',label:'아일랜드',w:120,h:80},
    {type:'dishwasher',label:'식기세척기',w:60,h:60},{type:'microwave',label:'전자레인지',w:50,h:35},
  ],
  '욕실': [
    {type:'toilet',label:'양변기',w:40,h:70},{type:'sink_b',label:'세면대',w:55,h:50},
    {type:'sink_b2',label:'세면대(2구)',w:110,h:50},{type:'bathtub',label:'욕조',w:80,h:170},
    {type:'shower',label:'샤워부스',w:90,h:90},{type:'urinal',label:'소변기',w:35,h:35},
  ],
  '식당/카페': [
    {type:'tbl_c2',label:'원형 2인',w:70,h:70},{type:'tbl_c4',label:'원형 4인',w:100,h:100},
    {type:'tbl_r2',label:'사각 2인',w:80,h:70},{type:'tbl_r4',label:'사각 4인',w:120,h:80},
    {type:'tbl_r6',label:'사각 6인',w:180,h:90},{type:'tbl_r8',label:'사각 8인',w:240,h:100},
    {type:'chair_r',label:'의자',w:45,h:45},{type:'bar_stool',label:'바 스툴',w:40,h:40},
    {type:'bar_counter',label:'바 카운터',w:300,h:60},{type:'cashier',label:'계산대',w:120,h:60},
  ],
  '사무실': [
    {type:'desk_s',label:'단독 책상',w:140,h:70},{type:'desk_l',label:'L형 책상',w:160,h:140},
    {type:'chair_o',label:'사무의자',w:55,h:55},{type:'meeting_tb',label:'회의테이블',w:240,h:100},
    {type:'partition',label:'파티션',w:120,h:12},{type:'locker',label:'사물함',w:90,h:50},
    {type:'printer',label:'복합기',w:55,h:50},{type:'shelf',label:'선반',w:90,h:35},
  ],
  '미용실': [
    {type:'styling_ch',label:'미용의자',w:65,h:70},{type:'mirror_st',label:'미용거울',w:80,h:50},
    {type:'shampoo',label:'샴푸볼',w:65,h:100},{type:'dryer_hood',label:'드라이어 후드',w:60,h:60},
    {type:'reception',label:'리셉션 데스크',w:120,h:60},{type:'waiting',label:'대기 의자',w:120,h:50},
  ],
  '구조': [
    {type:'stairs_u',label:'계단(상행)',w:100,h:200},{type:'stairs_d',label:'계단(하행)',w:100,h:200},
    {type:'elevator',label:'엘리베이터',w:140,h:150},{type:'column',label:'기둥',w:30,h:30},
    {type:'ramp',label:'경사로',w:100,h:150},
  ],
};

// ── 템플릿 (폴리곤 형식) ────────────────────────────────────────────────────────
const TEMPLATES = [
  { id:'t1', name:'소형 카페', emoji:'☕',
    rooms:[
      {id:'r1',name:'홀',points:rectPts(0,0,600,400),floorColor:'#fdf6e3',doors:[{id:'d1',edgeIdx:2,offset:250,width:90}],windows:[{id:'w1',edgeIdx:0,offset:80,width:150},{id:'w2',edgeIdx:0,offset:370,width:150}]},
      {id:'r2',name:'주방',points:rectPts(600,0,300,250),floorColor:'#f0f4ff',doors:[{id:'d2',edgeIdx:3,offset:80,width:80}],windows:[{id:'w3',edgeIdx:0,offset:80,width:120}]},
      {id:'r3',name:'화장실',points:rectPts(600,250,120,150),floorColor:'#f0fff4',doors:[{id:'d3',edgeIdx:3,offset:30,width:70}],windows:[]},
    ],
    items:[
      {id:'i1',type:'tbl_c4',x:80,y:50,width:100,height:100,rotation:0,label:'',color:'#c8a060'},
      {id:'i2',type:'tbl_c4',x:250,y:50,width:100,height:100,rotation:0,label:'',color:'#c8a060'},
      {id:'i3',type:'tbl_c4',x:420,y:50,width:100,height:100,rotation:0,label:'',color:'#c8a060'},
      {id:'i4',type:'tbl_c2',x:80,y:240,width:70,height:70,rotation:0,label:'',color:'#c8a060'},
      {id:'i5',type:'tbl_c2',x:220,y:240,width:70,height:70,rotation:0,label:'',color:'#c8a060'},
      {id:'i6',type:'tbl_c2',x:360,y:240,width:70,height:70,rotation:0,label:'',color:'#c8a060'},
      {id:'i7',type:'bar_counter',x:620,y:20,width:260,height:60,rotation:0,label:'바 카운터',color:'#7a5c2e'},
      {id:'i8',type:'stove',x:660,y:100,width:60,height:60,rotation:0,label:'',color:'#555'},
      {id:'i9',type:'sink_k',x:730,y:100,width:80,height:60,rotation:0,label:'',color:'#aaa'},
    ]
  },
  { id:'t2', name:'헤어샵', emoji:'💈',
    rooms:[
      {id:'r1',name:'메인홀',points:rectPts(0,0,700,350),floorColor:'#fff5f5',doors:[{id:'d1',edgeIdx:2,offset:300,width:100}],windows:[{id:'w1',edgeIdx:0,offset:50,width:200}]},
      {id:'r2',name:'샴푸실',points:rectPts(0,350,250,200),floorColor:'#f0fff0',doors:[{id:'d2',edgeIdx:0,offset:80,width:80}],windows:[]},
      {id:'r3',name:'화장실',points:rectPts(250,350,180,200),floorColor:'#f5f5ff',doors:[{id:'d3',edgeIdx:0,offset:55,width:70}],windows:[]},
    ],
    items:[
      {id:'i1',type:'mirror_st',x:30,y:20,width:80,height:50,rotation:0,label:'1번',color:'#e0d4c0'},
      {id:'i2',type:'mirror_st',x:130,y:20,width:80,height:50,rotation:0,label:'2번',color:'#e0d4c0'},
      {id:'i3',type:'mirror_st',x:230,y:20,width:80,height:50,rotation:0,label:'3번',color:'#e0d4c0'},
      {id:'i4',type:'mirror_st',x:330,y:20,width:80,height:50,rotation:0,label:'4번',color:'#e0d4c0'},
      {id:'i5',type:'mirror_st',x:430,y:20,width:80,height:50,rotation:0,label:'5번',color:'#e0d4c0'},
      {id:'i6',type:'styling_ch',x:42,y:85,width:65,height:70,rotation:0,label:'',color:'#3a7ad9'},
      {id:'i7',type:'styling_ch',x:142,y:85,width:65,height:70,rotation:0,label:'',color:'#3a7ad9'},
      {id:'i8',type:'styling_ch',x:242,y:85,width:65,height:70,rotation:0,label:'',color:'#3a7ad9'},
      {id:'i9',type:'styling_ch',x:342,y:85,width:65,height:70,rotation:0,label:'',color:'#3a7ad9'},
      {id:'i10',type:'styling_ch',x:442,y:85,width:65,height:70,rotation:0,label:'',color:'#3a7ad9'},
      {id:'i11',type:'reception',x:560,y:40,width:120,height:60,rotation:0,label:'접수대',color:'#7a5c2e'},
      {id:'i12',type:'waiting',x:555,y:200,width:120,height:50,rotation:0,label:'대기석',color:'#555'},
      {id:'i13',type:'shampoo',x:20,y:380,width:65,height:100,rotation:0,label:'',color:'#aaa'},
      {id:'i14',type:'shampoo',x:100,y:380,width:65,height:100,rotation:0,label:'',color:'#aaa'},
      {id:'i15',type:'shampoo',x:180,y:380,width:65,height:100,rotation:0,label:'',color:'#aaa'},
    ]
  },
  { id:'t3', name:'소형 음식점', emoji:'🍽️',
    rooms:[
      {id:'r1',name:'홀',points:rectPts(0,0,650,450),floorColor:'#fffbf0',doors:[{id:'d1',edgeIdx:2,offset:275,width:100}],windows:[{id:'w1',edgeIdx:0,offset:80,width:150}]},
      {id:'r2',name:'주방',points:rectPts(650,0,350,250),floorColor:'#f5fff5',doors:[{id:'d2',edgeIdx:3,offset:80,width:80}],windows:[]},
      {id:'r3',name:'화장실(남)',points:rectPts(650,250,150,150),floorColor:'#f0f0ff',doors:[{id:'d3',edgeIdx:3,offset:30,width:70}],windows:[]},
      {id:'r4',name:'화장실(여)',points:rectPts(800,250,150,150),floorColor:'#fff0f5',doors:[{id:'d4',edgeIdx:3,offset:30,width:70}],windows:[]},
    ],
    items:[
      {id:'i1',type:'tbl_r4',x:50,y:40,width:120,height:80,rotation:0,label:'1번',color:'#c8a060'},
      {id:'i2',type:'tbl_r4',x:230,y:40,width:120,height:80,rotation:0,label:'2번',color:'#c8a060'},
      {id:'i3',type:'tbl_r4',x:410,y:40,width:120,height:80,rotation:0,label:'3번',color:'#c8a060'},
      {id:'i4',type:'tbl_r4',x:50,y:200,width:120,height:80,rotation:0,label:'4번',color:'#c8a060'},
      {id:'i5',type:'tbl_r4',x:230,y:200,width:120,height:80,rotation:0,label:'5번',color:'#c8a060'},
      {id:'i6',type:'tbl_r4',x:410,y:200,width:120,height:80,rotation:0,label:'6번',color:'#c8a060'},
      {id:'i7',type:'counter',x:670,y:20,width:280,height:60,rotation:0,label:'주방카운터',color:'#7a5c2e'},
      {id:'i8',type:'stove',x:670,y:100,width:60,height:60,rotation:0,label:'',color:'#555'},
      {id:'i9',type:'sink_k',x:740,y:100,width:80,height:60,rotation:0,label:'',color:'#aaa'},
    ]
  },
  { id:'t4', name:'사무실', emoji:'💼',
    rooms:[
      {id:'r1',name:'사무공간',points:rectPts(0,0,700,500),floorColor:'#f8faff',doors:[{id:'d1',edgeIdx:2,offset:50,width:90}],windows:[{id:'w1',edgeIdx:0,offset:50,width:200}]},
      {id:'r2',name:'회의실',points:rectPts(700,0,250,200),floorColor:'#fff8f0',doors:[{id:'d2',edgeIdx:3,offset:60,width:80}],windows:[]},
      {id:'r3',name:'화장실',points:rectPts(700,200,150,150),floorColor:'#f0f0f5',doors:[{id:'d3',edgeIdx:3,offset:30,width:70}],windows:[]},
    ],
    items:[
      {id:'i1',type:'desk_s',x:50,y:40,width:140,height:70,rotation:0,label:'',color:'#e0d4c0'},
      {id:'i2',type:'desk_s',x:220,y:40,width:140,height:70,rotation:0,label:'',color:'#e0d4c0'},
      {id:'i3',type:'desk_s',x:390,y:40,width:140,height:70,rotation:0,label:'',color:'#e0d4c0'},
      {id:'i4',type:'desk_s',x:560,y:40,width:140,height:70,rotation:0,label:'',color:'#e0d4c0'},
      {id:'i5',type:'desk_s',x:50,y:190,width:140,height:70,rotation:0,label:'',color:'#e0d4c0'},
      {id:'i6',type:'desk_s',x:220,y:190,width:140,height:70,rotation:0,label:'',color:'#e0d4c0'},
      {id:'i7',type:'desk_s',x:390,y:190,width:140,height:70,rotation:0,label:'',color:'#e0d4c0'},
      {id:'i8',type:'desk_s',x:560,y:190,width:140,height:70,rotation:0,label:'',color:'#e0d4c0'},
      {id:'i9',type:'meeting_tb',x:720,y:30,width:200,height:120,rotation:0,label:'',color:'#d4c090'},
    ]
  },
  { id:'t5', name:'L형 공간 (샘플)', emoji:'📐',
    rooms:[
      {id:'r1',name:'메인 공간',
       points:[{x:0,y:0},{x:700,y:0},{x:700,y:300},{x:400,y:300},{x:400,y:600},{x:0,y:600}],
       floorColor:'#fdf8f0',doors:[{id:'d1',edgeIdx:4,offset:100,width:90}],windows:[{id:'w1',edgeIdx:0,offset:100,width:200},{id:'w2',edgeIdx:1,offset:50,width:150}]},
      {id:'r2',name:'창고',points:rectPts(400,300,300,300),floorColor:'#f5f5f0',doors:[{id:'d2',edgeIdx:3,offset:100,width:80}],windows:[]},
    ],
    items:[
      {id:'i1',type:'tbl_r6',x:100,y:100,width:180,height:90,rotation:0,label:'',color:'#c8a060'},
      {id:'i2',type:'tbl_r4',x:350,y:100,width:120,height:80,rotation:0,label:'',color:'#c8a060'},
      {id:'i3',type:'shelf',x:410,y:340,width:90,height:35,rotation:0,label:'',color:'#aaa'},
    ]
  },
];

// ── 가구 SVG 심볼 (FurnitureShape) ────────────────────────────────────────────
function FurnitureShape({ type, w, h, color='#e8dcc8', selected }) {
  const s = selected ? '#1d6ef5' : '#555';
  const sw = selected ? 2 : 1.2;
  const fill = color || '#e8dcc8';
  const cx=w/2, cy=h/2;
  const shapes = {
    sofa:()=><g><rect x={0} y={h*.35} width={w} height={h*.65} rx={4} fill={fill} stroke={s} strokeWidth={sw}/><rect x={0} y={0} width={w} height={h*.38} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={0} y={h*.35} width={w*.12} height={h*.65} rx={2} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.88} y={h*.35} width={w*.12} height={h*.65} rx={2} fill={fill} stroke={s} strokeWidth={sw}/>{[.13,.46,.79].map((px,i)=><rect key={i} x={w*px} y={h*.38} width={w*.26} height={h*.62} rx={3} fill={fill} stroke={s} strokeWidth={.8}/>)}</g>,
    sofa_l:()=><g><rect x={0} y={0} width={w*.45} height={h} rx={4} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.45} y={h*.55} width={w*.55} height={h*.45} rx={4} fill={fill} stroke={s} strokeWidth={sw}/></g>,
    armchair:()=><g><rect x={w*.1} y={h*.4} width={w*.8} height={h*.6} rx={4} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.1} y={0} width={w*.8} height={h*.43} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={0} y={h*.4} width={w*.12} height={h*.6} rx={2} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.88} y={h*.4} width={w*.12} height={h*.6} rx={2} fill={fill} stroke={s} strokeWidth={sw}/></g>,
    coffee_tb:()=><g><rect x={0} y={0} width={w} height={h} rx={5} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.08} y={h*.08} width={w*.84} height={h*.84} rx={3} fill="none" stroke={s} strokeWidth={.7} strokeDasharray="3,2"/></g>,
    tv_stand:()=><g><rect x={0} y={0} width={w} height={h} rx={2} fill={fill} stroke={s} strokeWidth={sw}/><line x1={w*.33} y1={0} x2={w*.33} y2={h} stroke={s} strokeWidth={.8}/><line x1={w*.66} y1={0} x2={w*.66} y2={h} stroke={s} strokeWidth={.8}/></g>,
    bookshelf:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/>{[.25,.5,.75].map((px,i)=><line key={i} x1={w*px} y1={0} x2={w*px} y2={h} stroke={s} strokeWidth={.7}/>)}</g>,
    side_tb:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><circle cx={cx} cy={cy} r={Math.min(w,h)*.3} fill="none" stroke={s} strokeWidth={.7}/></g>,
    bed_s:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.05} y={h*.05} width={w*.9} height={h*.2} rx={4} fill={fill} stroke={s} strokeWidth={1}/><line x1={0} y1={h*.28} x2={w} y2={h*.28} stroke={s} strokeWidth={.8}/><rect x={w*.1} y={h*.35} width={w*.8} height={h*.55} rx={3} fill={fill} stroke={s} strokeWidth={.8} strokeDasharray="4,2"/></g>,
    bed_d:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.05} y={h*.04} width={w*.42} height={h*.18} rx={4} fill={fill} stroke={s} strokeWidth={1}/><rect x={w*.53} y={h*.04} width={w*.42} height={h*.18} rx={4} fill={fill} stroke={s} strokeWidth={1}/><line x1={0} y1={h*.25} x2={w} y2={h*.25} stroke={s} strokeWidth={.8}/><rect x={w*.08} y={h*.32} width={w*.84} height={h*.56} rx={3} fill={fill} stroke={s} strokeWidth={.8} strokeDasharray="4,2"/></g>,
    bed_q:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.05} y={h*.04} width={w*.42} height={h*.17} rx={4} fill={fill} stroke={s} strokeWidth={1}/><rect x={w*.53} y={h*.04} width={w*.42} height={h*.17} rx={4} fill={fill} stroke={s} strokeWidth={1}/><line x1={0} y1={h*.24} x2={w} y2={h*.24} stroke={s} strokeWidth={.8}/><rect x={w*.06} y={h*.3} width={w*.88} height={h*.58} rx={3} fill={fill} stroke={s} strokeWidth={.8} strokeDasharray="4,2"/></g>,
    bed_k:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.04} y={h*.03} width={w*.43} height={h*.16} rx={4} fill={fill} stroke={s} strokeWidth={1}/><rect x={w*.53} y={h*.03} width={w*.43} height={h*.16} rx={4} fill={fill} stroke={s} strokeWidth={1}/><line x1={0} y1={h*.22} x2={w} y2={h*.22} stroke={s} strokeWidth={.8}/><rect x={w*.05} y={h*.28} width={w*.9} height={h*.6} rx={3} fill={fill} stroke={s} strokeWidth={.8} strokeDasharray="4,2"/></g>,
    wardrobe:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><line x1={cx} y1={0} x2={cx} y2={h} stroke={s} strokeWidth={1}/><circle cx={w*.35} cy={cy} r={3} fill={s}/><circle cx={w*.65} cy={cy} r={3} fill={s}/></g>,
    dresser:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><line x1={0} y1={h*.5} x2={w} y2={h*.5} stroke={s} strokeWidth={.8}/><line x1={cx} y1={0} x2={cx} y2={h} stroke={s} strokeWidth={.8}/>{[[.35,.25],[.65,.25],[.35,.75],[.65,.75]].map(([px,py],i)=><circle key={i} cx={w*px} cy={h*py} r={2.5} fill={s}/>)}</g>,
    nightstand:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><line x1={0} y1={h*.5} x2={w} y2={h*.5} stroke={s} strokeWidth={.8}/><circle cx={cx} cy={h*.25} r={2.5} fill={s}/><circle cx={cx} cy={h*.75} r={2.5} fill={s}/></g>,
    counter:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.03} y={h*.1} width={w*.94} height={h*.8} rx={2} fill="none" stroke={s} strokeWidth={.7} strokeDasharray="3,2"/></g>,
    counter_l:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><line x1={w*.6} y1={0} x2={w*.6} y2={h} stroke={s} strokeWidth={1}/></g>,
    sink_k:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.1} y={h*.12} width={w*.8} height={h*.76} rx={3} fill="#c8e8f8" stroke={s} strokeWidth={1}/><circle cx={cx} cy={cy} r={4} fill={s}/></g>,
    stove:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/>{[[.25,.28],[.75,.28],[.25,.72],[.75,.72]].map(([px,py],i)=><circle key={i} cx={w*px} cy={h*py} r={Math.min(w,h)*.16} fill="none" stroke={s} strokeWidth={1.2}/>)}</g>,
    refrigerator:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><line x1={0} y1={h*.35} x2={w} y2={h*.35} stroke={s} strokeWidth={1}/><circle cx={w*.8} cy={h*.18} r={3} fill={s}/><circle cx={w*.8} cy={h*.67} r={3} fill={s}/></g>,
    island:()=><g><rect x={0} y={0} width={w} height={h} rx={4} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.06} y={h*.1} width={w*.88} height={h*.8} rx={2} fill="none" stroke={s} strokeWidth={.8} strokeDasharray="4,2"/></g>,
    dishwasher:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><circle cx={cx} cy={cy} r={Math.min(w,h)*.25} fill="none" stroke={s} strokeWidth={.8}/></g>,
    microwave:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.06} y={h*.1} width={w*.72} height={h*.8} rx={2} fill="none" stroke={s} strokeWidth={.8}/><circle cx={w*.86} cy={cy} r={Math.min(w,h)*.2} fill="none" stroke={s} strokeWidth={.8}/></g>,
    toilet:()=><g><rect x={w*.05} y={0} width={w*.9} height={h*.32} rx={2} fill={fill} stroke={s} strokeWidth={sw}/><ellipse cx={cx} cy={h*.65} rx={w*.44} ry={h*.33} fill={fill} stroke={s} strokeWidth={sw}/><ellipse cx={cx} cy={h*.65} rx={w*.33} ry={h*.26} fill="#d8eef8" stroke={s} strokeWidth={.8}/></g>,
    sink_b:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><ellipse cx={cx} cy={cy} rx={w*.38} ry={h*.36} fill="#c8e8f8" stroke={s} strokeWidth={1}/><circle cx={cx} cy={cy} r={3} fill={s}/></g>,
    sink_b2:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><ellipse cx={w*.27} cy={cy} rx={w*.2} ry={h*.36} fill="#c8e8f8" stroke={s} strokeWidth={1}/><ellipse cx={w*.73} cy={cy} rx={w*.2} ry={h*.36} fill="#c8e8f8" stroke={s} strokeWidth={1}/></g>,
    bathtub:()=><g><rect x={0} y={0} width={w} height={h} rx={8} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.08} y={h*.07} width={w*.84} height={h*.86} rx={6} fill="#c8e8f8" stroke={s} strokeWidth={.8}/></g>,
    shower:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><path d={`M ${w*.1} ${h} Q 0 ${h} 0 ${h*.9} L 0 0 L ${w} 0 L ${w} ${h*.9} Q ${w} ${h} ${w*.9} ${h} Z`} fill="#c8e8f8" stroke={s} strokeWidth={.8}/><circle cx={cx} cy={cy} r={5} fill="none" stroke={s} strokeWidth={.8}/></g>,
    urinal:()=><g><ellipse cx={cx} cy={h*.6} rx={w*.4} ry={h*.38} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.2} y={0} width={w*.6} height={h*.4} rx={2} fill={fill} stroke={s} strokeWidth={.8}/></g>,
    tbl_c2:()=>{const r=Math.min(w,h)*.42;return<g><circle cx={cx} cy={cy} r={r} fill={fill} stroke={s} strokeWidth={sw}/><rect x={cx-8} y={2} width={16} height={10} rx={2} fill={s} opacity={.25}/><rect x={cx-8} y={h-12} width={16} height={10} rx={2} fill={s} opacity={.25}/></g>;},
    tbl_c4:()=>{const r=Math.min(w,h)*.38;return<g><circle cx={cx} cy={cy} r={r} fill={fill} stroke={s} strokeWidth={sw}/>{[[cx-8,2],[cx-8,h-12],[2,cy-8],[w-12,cy-8]].map(([x_,y_],i)=><rect key={i} x={x_} y={y_} width={16} height={10} rx={2} fill={s} opacity={.25} transform={i>=2?`rotate(90 ${x_+8} ${y_+5})`:undefined}/>)}</g>;},
    tbl_r2:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.2} y={2} width={w*.6} height={9} rx={2} fill={s} opacity={.25}/><rect x={w*.2} y={h-11} width={w*.6} height={9} rx={2} fill={s} opacity={.25}/></g>,
    tbl_r4:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.1} y={2} width={w*.35} height={9} rx={2} fill={s} opacity={.25}/><rect x={w*.55} y={2} width={w*.35} height={9} rx={2} fill={s} opacity={.25}/><rect x={w*.1} y={h-11} width={w*.35} height={9} rx={2} fill={s} opacity={.25}/><rect x={w*.55} y={h-11} width={w*.35} height={9} rx={2} fill={s} opacity={.25}/></g>,
    tbl_r6:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/>{[.08,.38,.68].map((px,i)=><g key={i}><rect x={w*px} y={2} width={w*.22} height={9} rx={2} fill={s} opacity={.25}/><rect x={w*px} y={h-11} width={w*.22} height={9} rx={2} fill={s} opacity={.25}/></g>)}</g>,
    tbl_r8:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/>{[.05,.3,.55,.78].map((px,i)=><g key={i}><rect x={w*px} y={2} width={w*.17} height={9} rx={2} fill={s} opacity={.25}/><rect x={w*px} y={h-11} width={w*.17} height={9} rx={2} fill={s} opacity={.25}/></g>)}</g>,
    chair_r:()=><g><rect x={w*.1} y={h*.35} width={w*.8} height={h*.65} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.1} y={0} width={w*.8} height={h*.38} rx={2} fill={fill} stroke={s} strokeWidth={sw}/><rect x={0} y={h*.35} width={w*.12} height={h*.65} rx={2} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.88} y={h*.35} width={w*.12} height={h*.65} rx={2} fill={fill} stroke={s} strokeWidth={sw}/></g>,
    bar_stool:()=><g><circle cx={cx} cy={cy} r={Math.min(w,h)*.42} fill={fill} stroke={s} strokeWidth={sw}/><circle cx={cx} cy={cy} r={Math.min(w,h)*.2} fill="none" stroke={s} strokeWidth={.8}/></g>,
    bar_counter:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><rect x={0} y={h*.6} width={w} height={h*.4} fill={s} opacity={.08}/><line x1={0} y1={h*.6} x2={w} y2={h*.6} stroke={s} strokeWidth={1}/>{Array.from({length:Math.floor(w/40)}).map((_,i)=><circle key={i} cx={20+i*40} cy={h*.3} r={4} fill={s} opacity={.2}/>)}</g>,
    cashier:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.05} y={h*.1} width={w*.55} height={h*.5} rx={2} fill="#c8e8f8" stroke={s} strokeWidth={.8}/><rect x={w*.65} y={h*.1} width={w*.3} height={h*.35} rx={2} fill={s} opacity={.2}/></g>,
    desk_s:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.05} y={h*.1} width={w*.7} height={h*.5} rx={2} fill="#c8e8f8" stroke={s} strokeWidth={.8}/><rect x={w*.78} y={h*.1} width={w*.17} height={h*.5} rx={2} fill={s} opacity={.2}/></g>,
    desk_l:()=><g><rect x={0} y={0} width={w} height={h*.45} fill={fill} stroke={s} strokeWidth={sw}/><rect x={0} y={h*.45} width={w*.5} height={h*.55} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.05} y={h*.05} width={w*.7} height={h*.35} rx={2} fill="#c8e8f8" stroke={s} strokeWidth={.8}/></g>,
    chair_o:()=><g><circle cx={cx} cy={cy*1.2} r={Math.min(w,h)*.38} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.2} y={0} width={w*.6} height={h*.28} rx={3} fill={fill} stroke={s} strokeWidth={sw}/></g>,
    meeting_tb:()=><g><rect x={0} y={0} width={w} height={h} rx={5} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.05} y={h*.1} width={w*.9} height={h*.8} rx={3} fill="none" stroke={s} strokeWidth={.8} strokeDasharray="4,3"/>{Array.from({length:Math.floor(w/50)}).map((_,i)=><g key={i}><rect x={10+i*50} y={2} width={30} height={8} rx={2} fill={s} opacity={.2}/><rect x={10+i*50} y={h-10} width={30} height={8} rx={2} fill={s} opacity={.2}/></g>)}</g>,
    partition:()=><g><rect x={0} y={0} width={w} height={h} fill={s} opacity={.6}/></g>,
    locker:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/>{[.33,.66].map((px,i)=><line key={i} x1={w*px} y1={0} x2={w*px} y2={h} stroke={s} strokeWidth={.8}/>)}{[.16,.5,.83].map((px,i)=><circle key={i} cx={w*px} cy={cy} r={2.5} fill={s} opacity={.5}/>)}</g>,
    printer:()=><g><rect x={0} y={0} width={w} height={h} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.1} y={h*.15} width={w*.8} height={h*.25} rx={1} fill="#c8e8f8" stroke={s} strokeWidth={.7}/></g>,
    shelf:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/>{[.25,.5,.75].map((px,i)=><line key={i} x1={w*px} y1={0} x2={w*px} y2={h} stroke={s} strokeWidth={.8}/>)}</g>,
    styling_ch:()=><g><circle cx={cx} cy={h*.55} r={Math.min(w,h)*.4} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.2} y={0} width={w*.6} height={h*.2} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.1} y={h*.85} width={w*.35} height={h*.12} rx={2} fill={s} opacity={.25}/><rect x={w*.55} y={h*.85} width={w*.35} height={h*.12} rx={2} fill={s} opacity={.25}/></g>,
    mirror_st:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.1} y={h*.06} width={w*.8} height={h*.65} rx={3} fill="#c8e8f8" stroke={s} strokeWidth={1}/><rect x={w*.05} y={h*.76} width={w*.9} height={h*.2} rx={2} fill={s} opacity={.15}/></g>,
    shampoo:()=><g><rect x={0} y={0} width={w} height={h} rx={4} fill={fill} stroke={s} strokeWidth={sw}/><ellipse cx={cx} cy={h*.45} rx={w*.38} ry={h*.28} fill="#c8e8f8" stroke={s} strokeWidth={1}/><rect x={w*.2} y={h*.75} width={w*.6} height={h*.18} rx={5} fill={fill} stroke={s} strokeWidth={1}/></g>,
    dryer_hood:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><ellipse cx={cx} cy={cy} rx={w*.4} ry={h*.4} fill={s} opacity={.15} stroke={s} strokeWidth={1}/><circle cx={cx} cy={cy} r={5} fill={s} opacity={.3}/></g>,
    reception:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><rect x={w*.05} y={h*.1} width={w*.6} height={h*.5} rx={2} fill="#c8e8f8" stroke={s} strokeWidth={.8}/></g>,
    waiting:()=><g><rect x={0} y={h*.35} width={w} height={h*.65} rx={3} fill={fill} stroke={s} strokeWidth={sw}/><rect x={0} y={0} width={w} height={h*.38} rx={2} fill={fill} stroke={s} strokeWidth={sw}/>{[.1,.4,.7].map((px,i)=><rect key={i} x={w*px} y={h*.38} width={w*.22} height={h*.62} rx={2} fill={fill} stroke={s} strokeWidth={.7}/>)}</g>,
    stairs_u:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/>{Array.from({length:8}).map((_,i)=><line key={i} x1={0} y1={h*(i+1)/9} x2={w} y2={h*(i+1)/9} stroke={s} strokeWidth={.8}/>)}<polygon points={`${cx},${h*.1} ${cx-8},${h*.25} ${cx+8},${h*.25}`} fill={s} opacity={.5}/></g>,
    stairs_d:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/>{Array.from({length:8}).map((_,i)=><line key={i} x1={0} y1={h*(i+1)/9} x2={w} y2={h*(i+1)/9} stroke={s} strokeWidth={.8}/>)}<polygon points={`${cx},${h*.9} ${cx-8},${h*.75} ${cx+8},${h*.75}`} fill={s} opacity={.5}/></g>,
    elevator:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><line x1={cx} y1={0} x2={cx} y2={h} stroke={s} strokeWidth={1}/><line x1={0} y1={cy} x2={w} y2={cy} stroke={s} strokeWidth={1}/><text x={w*.25} y={h*.35} textAnchor="middle" fontSize={Math.min(10,w*.15)} fill={s} fontWeight="600">EL</text><text x={w*.75} y={h*.35} textAnchor="middle" fontSize={Math.min(10,w*.15)} fill={s} fontWeight="600">EL</text></g>,
    column:()=><g><rect x={0} y={0} width={w} height={h} fill={s} opacity={.6}/><line x1={0} y1={0} x2={w} y2={h} stroke="#fff" strokeWidth={.8}/><line x1={w} y1={0} x2={0} y2={h} stroke="#fff" strokeWidth={.8}/></g>,
    ramp:()=><g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw}/><line x1={0} y1={h} x2={w} y2={0} stroke={s} strokeWidth={1.5}/>{Array.from({length:5}).map((_,i)=><line key={i} x1={w*(i+1)/6} y1={h} x2={w*(i+1)/6} y2={h*(1-(i+1)/6)} stroke={s} strokeWidth={.7} opacity={.4}/>)}</g>,
  };
  const draw = shapes[type];
  return draw ? draw() : <g><rect x={0} y={0} width={w} height={h} fill={fill} stroke={s} strokeWidth={sw} rx={2}/><text x={w/2} y={h/2+4} textAnchor="middle" fontSize={Math.min(11,w/4)} fill={s}>{type}</text></g>;
}

// ── 폴리곤 방 SVG 렌더러 ─────────────────────────────────────────────────────
function RoomPolygon({ room, scale:sc, bbMinX, bbMinY, selected, showVertices,
    onMouseDown, onVertexMouseDown }) {
  const pts = room.points.map(p=>({x:(p.x-bbMinX)*sc, y:(p.y-bbMinY)*sc}));
  const polyStr = pts.map(p=>`${p.x},${p.y}`).join(' ');
  const cen = polyCenter(pts);
  const area = polyArea(room.points);
  const wallPx = Math.max(2, WALL_W * sc / 100);

  // 문/창 렌더 (에지 인덱스 기반)
  const doorElems = (room.doors||[]).map((d,i)=>{
    const ei = d.edgeIdx ?? 0;
    const n = pts.length;
    const p1=pts[ei], p2=pts[(ei+1)%n];
    const dx=p2.x-p1.x, dy=p2.y-p1.y, elen=Math.hypot(dx,dy);
    if(elen<1) return null;
    const ux=dx/elen, uy=dy/elen;
    const off = (d.offset||50)*sc, dw=(d.width||90)*sc;
    const sx=p1.x+off*ux, sy=p1.y+off*(dy/elen);
    const ex=sx+dw*ux, ey=sy+dw*(dy/elen);
    // 내향 법선
    const nx=-dy/elen, ny=dx/elen;
    return (
      <g key={i}>
        <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#fff" strokeWidth={wallPx+2}/>
        <line x1={sx} y1={sy} x2={sx+dw*nx} y2={sy+dw*ny} stroke="#555" strokeWidth={1}/>
        <path d={`M ${sx+dw*nx} ${sy+dw*ny} A ${dw} ${dw} 0 0 1 ${ex} ${ey}`} fill="none" stroke="#555" strokeWidth={.8}/>
      </g>
    );
  });

  const winElems = (room.windows||[]).map((w,i)=>{
    const ei = w.edgeIdx ?? 0;
    const n = pts.length;
    const p1=pts[ei], p2=pts[(ei+1)%n];
    const dx=p2.x-p1.x, dy=p2.y-p1.y, elen=Math.hypot(dx,dy);
    if(elen<1) return null;
    const ux=dx/elen, uy=dy/elen;
    const off=(w.offset||50)*sc, ww=(w.width||120)*sc;
    const sx=p1.x+off*ux, sy=p1.y+off*uy;
    const ex=sx+ww*ux, ey=sy+ww*uy;
    return (
      <g key={i}>
        <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#fff" strokeWidth={wallPx}/>
        <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#6ab" strokeWidth={2}/>
        <line x1={sx+(-dy/elen)*2} y1={sy+(dx/elen)*2} x2={ex+(-dy/elen)*2} y2={ey+(dx/elen)*2} stroke="#6ab" strokeWidth={1} opacity={.5}/>
      </g>
    );
  });

  return (
    <g onMouseDown={onMouseDown} style={{cursor:'move',userSelect:'none'}}>
      {/* 바닥 채우기 */}
      <polygon points={polyStr} fill={room.floorColor||'#fdf8f0'} stroke="none"/>
      {/* 벽 */}
      <polygon points={polyStr} fill="none"
        stroke={selected?'#1d6ef5':'#2c2c2c'}
        strokeWidth={wallPx}
        strokeLinejoin="miter"/>
      {/* 선택 표시 */}
      {selected && <polygon points={polyStr} fill="none" stroke="#1d6ef5" strokeWidth={1.5} strokeDasharray="6,3" style={{pointerEvents:'none'}}/>}
      {/* 문/창 */}
      {doorElems}{winElems}
      {/* 방 이름 */}
      <text x={cen.x} y={cen.y-6} textAnchor="middle" fontSize={Math.max(10,sc*14)} fontWeight="600" fill="#333" style={{pointerEvents:'none'}}>{room.name}</text>
      <text x={cen.x} y={cen.y+10} textAnchor="middle" fontSize={Math.max(8,sc*10)} fill="#888" style={{pointerEvents:'none'}}>{area.toFixed(1)}m²</text>
      {/* 꼭짓점 핸들 (선택 시) */}
      {showVertices && pts.map((p,i)=>(
        <rect key={i}
          x={p.x-VTXR/2} y={p.y-VTXR/2} width={VTXR} height={VTXR}
          fill="#1d6ef5" stroke="#fff" strokeWidth={1.5}
          rx={2} style={{cursor:'pointer'}}
          onMouseDown={e=>{ e.stopPropagation(); onVertexMouseDown(e,i); }}/>
      ))}
    </g>
  );
}

// ── 문/창 편집 모달 ───────────────────────────────────────────────────────────
function RoomDetailModal({ room, onUpdate, onClose }) {
  const n = room.points.length;
  const edgeLabels = room.points.map((_,i)=>`벽 ${i+1}`);
  const [name,setName]=useState(room.name);
  const [floorColor,setFloor]=useState(room.floorColor||'#fdf8f0');
  const [doors,setDoors]=useState(room.doors||[]);
  const [windows,setWindows]=useState(room.windows||[]);

  function save(){ onUpdate({...room,name,floorColor,doors,windows}); }
  function addDoor(){ setDoors(d=>[...d,{id:uid(),edgeIdx:0,offset:50,width:90}]); }
  function updDoor(id,p){ setDoors(d=>d.map(x=>x.id===id?{...x,...p}:x)); }
  function delDoor(id){ setDoors(d=>d.filter(x=>x.id!==id)); }
  function addWin(){ setWindows(w=>[...w,{id:uid(),edgeIdx:0,offset:50,width:120}]); }
  function updWin(id,p){ setWindows(w=>w.map(x=>x.id===id?{...x,...p}:x)); }
  function delWin(id){ setWindows(w=>w.filter(x=>x.id!==id)); }

  const inp={width:'100%',padding:'6px 8px',border:'1px solid #ddd',borderRadius:6,fontSize:12,boxSizing:'border-box'};
  const row={display:'flex',gap:5,alignItems:'center',marginBottom:6,fontSize:11};
  const selSt={fontSize:11,padding:'3px 5px',border:'1px solid #ddd',borderRadius:4};
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}
         onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'#fff',borderRadius:14,padding:22,width:500,maxWidth:'95vw',maxHeight:'85vh',overflow:'auto',boxShadow:'0 8px 32px rgba(0,0,0,0.18)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:15}}>🚪 문/창문 편집</div>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#888'}}>×</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
          <div><div style={{fontSize:10,color:'#888',marginBottom:3}}>방 이름</div><input value={name} onChange={e=>setName(e.target.value)} style={inp}/></div>
          <div><div style={{fontSize:10,color:'#888',marginBottom:3}}>바닥 색상</div><input type="color" value={floorColor} onChange={e=>setFloor(e.target.value)} style={{width:'100%',height:32,border:'1px solid #ddd',borderRadius:6,cursor:'pointer'}}/></div>
        </div>
        {/* 문 */}
        <div style={{borderTop:'1px solid #f0f0f0',paddingTop:10,marginBottom:10}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
            <div style={{fontWeight:600,fontSize:12}}>🚪 문 ({doors.length}개)</div>
            <button onClick={addDoor} style={{fontSize:10,padding:'2px 8px',border:'1px solid #4f8ef7',borderRadius:5,background:'#f0f5ff',color:'#4f8ef7',cursor:'pointer'}}>+ 추가</button>
          </div>
          {doors.map(d=>(
            <div key={d.id} style={row}>
              <select value={d.edgeIdx??0} onChange={e=>updDoor(d.id,{edgeIdx:parseInt(e.target.value)})} style={{...selSt,flex:'0 0 65px'}}>
                {edgeLabels.map((l,i)=><option key={i} value={i}>{l}</option>)}
              </select>
              <label style={{display:'flex',alignItems:'center',gap:2}}>위치<input type="number" value={d.offset} min={0} onChange={e=>updDoor(d.id,{offset:parseInt(e.target.value)||0})} style={{width:46,padding:'3px 4px',border:'1px solid #ddd',borderRadius:4,fontSize:11}}/>cm</label>
              <label style={{display:'flex',alignItems:'center',gap:2}}>폭<input type="number" value={d.width} min={60} max={300} onChange={e=>updDoor(d.id,{width:parseInt(e.target.value)||0})} style={{width:42,padding:'3px 4px',border:'1px solid #ddd',borderRadius:4,fontSize:11}}/>cm</label>
              <button onClick={()=>delDoor(d.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#f44',fontSize:16,padding:0}}>×</button>
            </div>
          ))}
        </div>
        {/* 창문 */}
        <div style={{borderTop:'1px solid #f0f0f0',paddingTop:10,marginBottom:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
            <div style={{fontWeight:600,fontSize:12}}>🪟 창문 ({windows.length}개)</div>
            <button onClick={addWin} style={{fontSize:10,padding:'2px 8px',border:'1px solid #6ab',borderRadius:5,background:'#f0f8ff',color:'#5599aa',cursor:'pointer'}}>+ 추가</button>
          </div>
          {windows.map(w=>(
            <div key={w.id} style={row}>
              <select value={w.edgeIdx??0} onChange={e=>updWin(w.id,{edgeIdx:parseInt(e.target.value)})} style={{...selSt,flex:'0 0 65px'}}>
                {edgeLabels.map((l,i)=><option key={i} value={i}>{l}</option>)}
              </select>
              <label style={{display:'flex',alignItems:'center',gap:2}}>위치<input type="number" value={w.offset} min={0} onChange={e=>updWin(w.id,{offset:parseInt(e.target.value)||0})} style={{width:46,padding:'3px 4px',border:'1px solid #ddd',borderRadius:4,fontSize:11}}/>cm</label>
              <label style={{display:'flex',alignItems:'center',gap:2}}>폭<input type="number" value={w.width} min={30} onChange={e=>updWin(w.id,{width:parseInt(e.target.value)||0})} style={{width:42,padding:'3px 4px',border:'1px solid #ddd',borderRadius:4,fontSize:11}}/>cm</label>
              <button onClick={()=>delWin(w.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#f44',fontSize:16,padding:0}}>×</button>
            </div>
          ))}
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{padding:'7px 18px',borderRadius:7,border:'1px solid #ddd',background:'#fff',cursor:'pointer',fontSize:13}}>취소</button>
          <button onClick={save} style={{padding:'7px 22px',borderRadius:7,border:'none',background:'#1d6ef5',color:'#fff',cursor:'pointer',fontSize:13,fontWeight:700}}>저장</button>
        </div>
      </div>
    </div>
  );
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
export default function FloorPlanEditor({ project, onSave }) {
  const initRooms = (project.floorPlan?.rooms || []).map(migrateRoom);
  const [rooms,    setRooms]    = useState(initRooms);
  const [items,    setItems]    = useState(project.floorPlan?.items || []);
  const [scale,    setScale]    = useState(1.2);
  const [mode,     setMode]     = useState('select'); // select | drawPoly | partition
  const [drawPts,  setDrawPts]  = useState([]);       // 그리는 중인 꼭짓점들
  const [hoverPt,  setHoverPt]  = useState(null);     // 마우스 위치 (cm)
  const [partSt,   setPartSt]   = useState(null);     // partition 시작점 {roomId, edgePt}
  const [selId,    setSelId]    = useState(null);
  const [selType,  setSelType]  = useState(null);     // room | item
  const [drag,     setDrag]     = useState(null);     // {id,type,startX,startY,origPts or origX,origY}
  const [leftTab,  setLeftTab]  = useState('rooms');
  const [activeCat,setActiveCat]= useState('거실');
  const [pendingType,setPending] = useState(null);
  const [detailRoom,setDetail]  = useState(null);
  const [showTpl,  setShowTpl]  = useState(false);
  const svgRef = useRef(null);

  // ── Undo / Redo history ─────────────────────────────────────────────────────
  // rooms + items를 묶어 한 스냅샷으로 저장. 최대 50개 유지.
  const HISTORY_LIMIT = 50;
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const dragSnapRef = useRef(null); // 드래그 시작 시점 스냅샷 (mouseup에 한 번만 push)

  const commit = useCallback((nextRooms, nextItems) => {
    setPast(p => {
      const np = [...p, { rooms, items }];
      return np.length > HISTORY_LIMIT ? np.slice(np.length - HISTORY_LIMIT) : np;
    });
    setFuture([]);
    setRooms(nextRooms);
    setItems(nextItems);
    if (onSave) onSave({ ...project, floorPlan: { rooms: nextRooms, items: nextItems } });
  }, [rooms, items, onSave, project]);

  const undo = useCallback(() => {
    setPast(p => {
      if (p.length === 0) return p;
      const prev = p[p.length - 1];
      setFuture(f => [{ rooms, items }, ...f]);
      setRooms(prev.rooms);
      setItems(prev.items);
      if (onSave) onSave({ ...project, floorPlan: { rooms: prev.rooms, items: prev.items } });
      return p.slice(0, -1);
    });
  }, [rooms, items, onSave, project]);

  const redo = useCallback(() => {
    setFuture(f => {
      if (f.length === 0) return f;
      const nxt = f[0];
      setPast(p => [...p, { rooms, items }]);
      setRooms(nxt.rooms);
      setItems(nxt.items);
      if (onSave) onSave({ ...project, floorPlan: { rooms: nxt.rooms, items: nxt.items } });
      return f.slice(1);
    });
  }, [rooms, items, onSave, project]);

  // ESC 키 처리
  useEffect(()=>{
    const h = e=>{ if(e.key==='Escape'){ setMode('select'); setDrawPts([]); setPartSt(null); setPending(null); }};
    window.addEventListener('keydown',h);
    return ()=>window.removeEventListener('keydown',h);
  },[]);

  // Ctrl+Z / Ctrl+Y(또는 Ctrl+Shift+Z) 단축키 — input/textarea 포커스 시는 브라우저 기본 undo 우선
  useEffect(() => {
    function onKey(e) {
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  function save(r=rooms,i=items) { if(onSave) onSave({...project,floorPlan:{rooms:r,items:i}}); }

  // ── 방 CRUD (모든 변경은 commit으로 — undo/redo 자동) ─────────────────────
  function addRect() {
    // 현재 캔버스 중앙 근처에 300×250 방 추가
    const cx = rooms.length>0 ? Math.max(...rooms.flatMap(r=>r.points.map(p=>p.x)))+50 : 50;
    const r={id:uid(),name:'새 방',points:rectPts(cx,50,300,250),floorColor:'#fdf8f0',doors:[],windows:[]};
    const next=[...rooms,r];
    commit(next, items); setSelId(r.id); setSelType('room');
  }
  function updateRoom(id,patch) {
    const next=rooms.map(r=>r.id===id?{...r,...patch}:r);
    commit(next, items);
  }
  function deleteRoom(id) {
    const next=rooms.filter(r=>r.id!==id);
    commit(next, items);
    if(selId===id){setSelId(null);setSelType(null);}
  }

  // ── 가구 CRUD ─────────────────────────────────────────────────────────────
  function startPlace(type){ setPending(type); setMode('select'); }
  function updateItem(id,patch){ const next=items.map(it=>it.id===id?{...it,...patch}:it); commit(rooms, next); }
  function deleteItem(id){ const next=items.filter(it=>it.id!==id); commit(rooms, next); if(selId===id){setSelId(null);setSelType(null);}}
  function duplicateItem(id){ const it=items.find(x=>x.id===id); if(!it)return; const cp={...it,id:uid(),x:it.x+30,y:it.y+30}; const next=[...items,cp]; commit(rooms, next); setSelId(cp.id); setSelType('item'); }

  // ── 템플릿 ─────────────────────────────────────────────────────────────────
  function applyTemplate(tpl) {
    const migRooms = tpl.rooms.map(migrateRoom);
    commit(migRooms, tpl.items);
    setSelId(null); setSelType(null); setShowTpl(false);
  }

  // ── SVG 좌표 변환 ──────────────────────────────────────────────────────────
  const bb = (() => {
    const allPts = [...rooms.flatMap(r=>r.points), ...drawPts,
      ...items.flatMap(it=>[{x:it.x,y:it.y},{x:it.x+it.width,y:it.y+it.height}])];
    if(allPts.length===0) return {minX:-50,minY:-50,maxX:800,maxY:600};
    let minX=Infinity,minY=Infinity,maxX=-Infinity,maxY=-Infinity;
    allPts.forEach(p=>{minX=Math.min(minX,p.x);minY=Math.min(minY,p.y);maxX=Math.max(maxX,p.x);maxY=Math.max(maxY,p.y);});
    return {minX:minX-100,minY:minY-100,maxX:maxX+100,maxY:maxY+100};
  })();
  const svgW = Math.max((bb.maxX-bb.minX)*scale,600);
  const svgH = Math.max((bb.maxY-bb.minY)*scale,500);

  function svgToCm(svgX,svgY) { return {x:svgX/scale+bb.minX, y:svgY/scale+bb.minY}; }
  function getSVGXY(e) {
    const rect=svgRef.current?.getBoundingClientRect();
    if(!rect) return {x:0,y:0};
    return {x:e.clientX-rect.left, y:e.clientY-rect.top};
  }
  function getSVGCm(e) { const {x,y}=getSVGXY(e); return svgToCm(x,y); }

  // ── 드래그 (mousedown에 스냅샷 / mouseup에 history 1건만 push) ──────────
  function onRoomMouseDown(e,room) {
    if(mode!=='select') return;
    e.stopPropagation();
    setSelId(room.id); setSelType('room'); setPending(null);
    const {x,y}=getSVGXY(e);
    dragSnapRef.current = { rooms, items };
    setDrag({id:room.id,type:'room',startSX:x,startSY:y,origPts:room.points.map(p=>({...p}))});
  }
  function onVertexMouseDown(e,room,vi) {
    e.stopPropagation();
    setSelId(room.id); setSelType('room');
    const {x,y}=getSVGXY(e);
    dragSnapRef.current = { rooms, items };
    setDrag({id:room.id,type:'vertex',vi,startSX:x,startSY:y,origPts:room.points.map(p=>({...p}))});
  }
  function onItemMouseDown(e,it) {
    if(mode!=='select') return;
    e.stopPropagation();
    setSelId(it.id); setSelType('item'); setPending(null);
    const {x,y}=getSVGXY(e);
    dragSnapRef.current = { rooms, items };
    setDrag({id:it.id,type:'item',startSX:x,startSY:y,origX:it.x,origY:it.y});
  }

  function onSVGMouseMove(e) {
    const {x:sx,y:sy}=getSVGXY(e);
    const cm=svgToCm(sx,sy);
    setHoverPt({x:snapV(cm.x),y:snapV(cm.y),sx,sy});

    if(!drag) return;
    const dsx=sx-drag.startSX, dsy=sy-drag.startSY;
    const dcx=dsx/scale, dcy=dsy/scale;

    if(drag.type==='room') {
      const next=rooms.map(r=>r.id===drag.id?{...r,points:drag.origPts.map(p=>({x:snapV(p.x+dcx),y:snapV(p.y+dcy)}))}:r);
      setRooms(next);
    } else if(drag.type==='vertex') {
      const next=rooms.map(r=>{
        if(r.id!==drag.id) return r;
        const pts=[...r.points.map(p=>({...p}))];
        pts[drag.vi]={x:snapV(drag.origPts[drag.vi].x+dcx),y:snapV(drag.origPts[drag.vi].y+dcy)};
        return {...r,points:pts};
      });
      setRooms(next);
    } else if(drag.type==='item') {
      const next=items.map(it=>it.id===drag.id?{...it,x:snapV(drag.origX+dcx),y:snapV(drag.origY+dcy)}:it);
      setItems(next);
    }
  }
  function onSVGMouseUp() {
    if(drag){
      // 드래그 종료 시 history 1건만 push (시작 스냅샷 → past). 위치 변화가 있을 때만.
      const snap = dragSnapRef.current;
      dragSnapRef.current = null;
      if (snap) {
        const changed = JSON.stringify(snap.rooms) !== JSON.stringify(rooms) ||
                        JSON.stringify(snap.items) !== JSON.stringify(items);
        if (changed) {
          setPast(p => {
            const np = [...p, snap];
            return np.length > HISTORY_LIMIT ? np.slice(np.length - HISTORY_LIMIT) : np;
          });
          setFuture([]);
        }
      }
      save(rooms,items);
      setDrag(null);
    }
  }

  // ── 캔버스 클릭 ───────────────────────────────────────────────────────────
  function onSVGClick(e) {
    if(drag) return;
    const cm=getSVGCm(e);
    const snapCm={x:snapV(cm.x),y:snapV(cm.y)};

    if(mode==='drawPoly') {
      if(drawPts.length>=3) {
        // 첫 점과 가까우면 폴리곤 닫기
        const fp=drawPts[0];
        
        const sx=(snapCm.x-bb.minX)*scale, sy=(snapCm.y-bb.minY)*scale;
        const fpX=(fp.x-bb.minX)*scale, fpY=(fp.y-bb.minY)*scale;
        if(Math.hypot(sx-fpX,sy-fpY)<CLOSE_D) { closePoly(); return; }
      }
      setDrawPts(prev=>[...prev,snapCm]);
      return;
    }

    if(mode==='partition') {
      const clickedRoom=rooms.find(r=>ptInPoly(cm.x,cm.y,r.points));
      if(!clickedRoom) return;
      const ep=nearestEdgePt(clickedRoom.points,cm.x,cm.y);
      if(!ep) return;
      if(!partSt) {
        setPartSt({roomId:clickedRoom.id,edgePt:ep});
      } else if(partSt.roomId===clickedRoom.id) {
        // 두 번째 클릭 → 분할
        doPartition(clickedRoom,partSt.edgePt,ep);
        setPartSt(null); setMode('select');
      } else {
        // 다른 방 클릭 → 시작점 변경
        setPartSt({roomId:clickedRoom.id,edgePt:ep});
      }
      return;
    }

    // select mode
    if(pendingType) {
      const def=Object.values(FLIB).flat().find(f=>f.type===pendingType);
      if(def){
        const it={id:uid(),type:pendingType,x:snapCm.x,y:snapCm.y,width:def.w,height:def.h,rotation:0,label:def.label,color:'#e8dcc8'};
        const next=[...items,it]; commit(rooms, next);
        setSelId(it.id); setSelType('item'); setPending(null);
      }
      return;
    }
    // 빈 공간 클릭 → 선택 해제
    if(e.target===svgRef.current){ setSelId(null); setSelType(null); }
  }

  function onSVGDblClick(e) {
    if(mode==='drawPoly' && drawPts.length>=3){ closePoly(); }
  }

  function closePoly() {
    if(drawPts.length<3) return;
    const r={id:uid(),name:'새 방',points:drawPts,floorColor:'#fdf8f0',doors:[],windows:[]};
    const next=[...rooms,r]; commit(next, items);
    setSelId(r.id); setSelType('room');
    setDrawPts([]); setMode('select');
  }

  function doPartition(room,ep1,ep2) {
    if(ep1.edgeIdx===ep2.edgeIdx && Math.abs(ep1.t-ep2.t)<0.01) return;
    const result=splitPolygon(room.points,ep1,ep2);
    if(!result) return;
    const [a,b]=result;
    const r1={...room,id:uid(),name:room.name+' (1)',points:a};
    const r2={...room,id:uid(),name:room.name+' (2)',points:b};
    const next=rooms.filter(r=>r.id!==room.id);
    next.push(r1,r2);
    commit(next, items);
    setSelId(r1.id); setSelType('room');
  }

  // ── 통계 ──────────────────────────────────────────────────────────────────
  const totalM2=rooms.reduce((s,r)=>s+polyArea(r.points),0);
  const selectedRoom=selType==='room'?rooms.find(r=>r.id===selId):null;
  const selectedItem=selType==='item'?items.find(it=>it.id===selId):null;

  // ── 색상 팔레트 ────────────────────────────────────────────────────────────
  const floorColors=['#fdf8f0','#f0f4ff','#f0fff4','#fff0f5','#fffbf0','#f5f5f5','#e8f4f8','#fff5e6'];

  const modeBtn = (m,icon,label) => (
    <button onClick={()=>{setMode(m);setDrawPts([]);setPartSt(null);}}
      title={label}
      style={{display:'flex',alignItems:'center',gap:4,padding:'5px 10px',borderRadius:7,border:'none',cursor:'pointer',fontSize:11,fontWeight:600,
        background:mode===m?'#1d6ef5':'#f0f0f0',color:mode===m?'#fff':'#555',transition:'all .12s'}}>
      {icon}<span>{label}</span>
    </button>
  );

  function exportPNG() {
    const svg = svgRef.current; if(!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data],{type:'image/svg+xml'});
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = svg.clientWidth*2; canvas.height = svg.clientHeight*2;
      const ctx = canvas.getContext('2d');
      ctx.scale(2,2); ctx.fillStyle='#fff'; ctx.fillRect(0,0,canvas.width,canvas.height);
      ctx.drawImage(img,0,0,svg.clientWidth,svg.clientHeight);
      URL.revokeObjectURL(url);
      const a = document.createElement('a');
      a.href=canvas.toDataURL('image/png');
      a.download=(project.customerName||'도면')+'_평면도.png'; a.click();
    };
    img.src = url;
  }


  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',background:'#f5f6f8',fontFamily:'system-ui,sans-serif'}} tabIndex={0}
      onKeyDown={e=>{if(e.key==='Escape'){setMode('select');setDrawPts([]);setPartSt(null);setPending(null);}}}>

      {/* ── 툴바 ──────────────────────────────────────────────────────────── */}
      <div style={{display:'flex',alignItems:'center',gap:6,padding:'7px 12px',background:'#fff',borderBottom:'1px solid #e8e8e8',flexShrink:0,flexWrap:'wrap'}}>
        <div style={{fontWeight:700,fontSize:13,color:'#333',marginRight:4}}>📐 평면도 에디터</div>
        {rooms.length>0&&<div style={{fontSize:11,color:'#888'}}>총 {totalM2.toFixed(1)}m² ({(totalM2/3.306).toFixed(1)}평)</div>}
        <div style={{flex:1}}/>
        {/* Undo / Redo */}
        <button onClick={undo} disabled={past.length===0} title="실행취소 (Ctrl+Z)"
          style={{width:28,height:28,borderRadius:6,border:'1px solid #ddd',
            background:past.length===0?'#f5f5f5':'#fff',color:past.length===0?'#ccc':'#555',
            cursor:past.length===0?'not-allowed':'pointer',fontSize:13,fontWeight:600}}>↶</button>
        <button onClick={redo} disabled={future.length===0} title="다시실행 (Ctrl+Y)"
          style={{width:28,height:28,borderRadius:6,border:'1px solid #ddd',
            background:future.length===0?'#f5f5f5':'#fff',color:future.length===0?'#ccc':'#555',
            cursor:future.length===0?'not-allowed':'pointer',fontSize:13,fontWeight:600}}>↷</button>
        {/* 도구 버튼 */}
        {modeBtn('select','↖','선택')}
        {modeBtn('drawPoly','✏️','벽 그리기')}
        {modeBtn('partition','✂️','구획 나누기')}
        {/* 안내 */}
        {mode==='drawPoly'&&<div style={{fontSize:10,color:'#1d6ef5',background:'#f0f5ff',padding:'4px 8px',borderRadius:6}}>클릭으로 꼭짓점 추가 · 시작점 클릭(또는 더블클릭)으로 완료 · ESC 취소</div>}
        {mode==='partition'&&<div style={{fontSize:10,color:'#EA580C',background:'#fffbe6',padding:'4px 8px',borderRadius:6}}>{partSt?'두 번째 벽 점 클릭으로 분할':'벽 위를 클릭해 시작점 설정'}</div>}
        {pendingType&&<div style={{fontSize:10,color:'#7c3aed',background:'#f5f3ff',padding:'4px 8px',borderRadius:6}}>캔버스 클릭으로 배치 · ESC 취소</div>}
        <div style={{display:'flex',alignItems:'center',gap:3}}>
          <button onClick={()=>setScale(s=>Math.max(MIN_SC,s-.2))} style={{width:24,height:24,border:'1px solid #ddd',borderRadius:5,background:'#fff',cursor:'pointer',fontSize:14}}>−</button>
          <span style={{fontSize:11,color:'#555',minWidth:38,textAlign:'center'}}>{Math.round(scale*100)}%</span>
          <button onClick={()=>setScale(s=>Math.min(MAX_SC,s+.2))} style={{width:24,height:24,border:'1px solid #ddd',borderRadius:5,background:'#fff',cursor:'pointer',fontSize:14}}>+</button>
        </div>
        <button onClick={()=>setShowTpl(true)} style={{padding:'5px 10px',borderRadius:7,border:'1px solid #ddd',background:'#fff',fontSize:11,cursor:'pointer'}}>📋 템플릿</button>
        <button onClick={exportPNG} style={{padding:'5px 10px',borderRadius:7,border:'none',background:'#1d6ef5',color:'#fff',fontSize:11,cursor:'pointer',fontWeight:700}}>⬇ PNG</button>
      </div>

      <div style={{display:'flex',flex:1,overflow:'hidden'}}>
        {/* ── 좌측 패널 ──────────────────────────────────────────────────── */}
        <div style={{width:200,background:'#fff',borderRight:'1px solid #e8e8e8',display:'flex',flexDirection:'column',flexShrink:0}}>
          <div style={{display:'flex',padding:'6px 6px 0',borderBottom:'1px solid #eee',gap:3}}>
            {[['rooms','🏠 방'],['furniture','🛋 가구']].map(([k,l])=>(
              <button key={k} onClick={()=>setLeftTab(k)}
                style={{flex:1,padding:'6px 2px',border:'none',cursor:'pointer',fontSize:11,fontWeight:600,borderRadius:5,
                  background:leftTab===k?'#1d6ef5':'transparent',color:leftTab===k?'#fff':'#888'}}>
                {l}
              </button>
            ))}
          </div>

          {leftTab==='rooms' && (
            <div style={{flex:1,overflow:'auto',padding:'8px 6px'}}>
              <div style={{display:'flex',gap:4,marginBottom:8}}>
                <button onClick={addRect} style={{flex:1,padding:'7px',borderRadius:7,border:'none',background:'#1d6ef5',color:'#fff',fontSize:11,cursor:'pointer',fontWeight:700}}>+ 사각형</button>
                <button onClick={()=>setMode('drawPoly')} style={{flex:1,padding:'7px',borderRadius:7,border:'1px solid #1d6ef5',background:'#f0f5ff',color:'#1d6ef5',fontSize:11,cursor:'pointer',fontWeight:700}}>✏️ 그리기</button>
              </div>
              {rooms.length===0&&<div style={{fontSize:11,color:'#bbb',textAlign:'center',padding:'20px 0'}}>방을 추가하거나<br/>템플릿을 사용하세요</div>}
              {rooms.map(room=>(
                <div key={room.id} onClick={()=>{setSelId(room.id);setSelType('room');}}
                  style={{padding:'7px 8px',borderRadius:8,marginBottom:5,cursor:'pointer',
                    border:'1px solid',borderColor:selId===room.id?'#1d6ef5':'#e8e8e8',
                    background:selId===room.id?'#f0f5ff':'#fafafa'}}>
                  <div style={{fontWeight:600,fontSize:12,color:'#333'}}>{room.name}</div>
                  <div style={{fontSize:10,color:'#999',marginTop:2}}>{polyArea(room.points).toFixed(1)}m² · {room.points.length}꼭짓점</div>
                  <div style={{fontSize:10,color:'#aaa'}}>문 {(room.doors||[]).length} · 창 {(room.windows||[]).length}</div>
                </div>
              ))}
              {rooms.length>0&&<div style={{marginTop:6,padding:'7px',background:'#f8f8f8',borderRadius:7,fontSize:11,color:'#777'}}>합계: {totalM2.toFixed(2)}m² ({(totalM2/3.306).toFixed(1)}평)</div>}
            </div>
          )}

          {leftTab==='furniture' && (
            <div style={{flex:1,overflow:'auto'}}>
              <div style={{display:'flex',flexWrap:'wrap',gap:2,padding:'5px 5px 0'}}>
                {Object.keys(FLIB).map(cat=>(
                  <button key={cat} onClick={()=>setActiveCat(cat)}
                    style={{fontSize:9,padding:'2px 6px',borderRadius:4,border:'none',cursor:'pointer',
                      background:activeCat===cat?'#1d6ef5':'#f0f0f0',color:activeCat===cat?'#fff':'#555',fontWeight:activeCat===cat?700:400}}>
                    {cat}
                  </button>
                ))}
              </div>
              <div style={{padding:'5px'}}>
                {(FLIB[activeCat]||[]).map(fdef=>(
                  <div key={fdef.type} onClick={()=>startPlace(fdef.type)}
                    style={{display:'flex',alignItems:'center',gap:7,padding:'5px 7px',borderRadius:6,marginBottom:2,cursor:'pointer',
                      border:'1px solid',borderColor:pendingType===fdef.type?'#1d6ef5':'#e8e8e8',
                      background:pendingType===fdef.type?'#f0f5ff':'#fff'}}
                    onMouseEnter={e=>e.currentTarget.style.background='#f5f5f5'}
                    onMouseLeave={e=>e.currentTarget.style.background=pendingType===fdef.type?'#f0f5ff':'#fff'}>
                    <div style={{width:34,height:26,flexShrink:0,border:'1px solid #ddd',borderRadius:3,overflow:'hidden',background:'#fafafa'}}>
                      <svg width={34} height={26} viewBox={`0 0 ${fdef.w} ${fdef.h}`} preserveAspectRatio="xMidYMid meet">
                        <FurnitureShape type={fdef.type} w={fdef.w} h={fdef.h} color="#e8dcc8"/>
                      </svg>
                    </div>
                    <div>
                      <div style={{fontSize:11,fontWeight:600,color:'#333'}}>{fdef.label}</div>
                      <div style={{fontSize:9,color:'#aaa'}}>{fdef.w}×{fdef.h}cm</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── 캔버스 ─────────────────────────────────────────────────────── */}
        <div style={{flex:1,overflow:'auto',position:'relative',background:'#eef0f3',
          cursor:mode==='drawPoly'?'crosshair':mode==='partition'?'cell':pendingType?'crosshair':drag?'grabbing':'default'}}>
          {rooms.length===0&&drawPts.length===0&&(
            <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',textAlign:'center',color:'#bbb',pointerEvents:'none'}}>
              <div style={{fontSize:44,marginBottom:10}}>📐</div>
              <div style={{fontSize:13}}>좌측의 <b>✏️ 그리기</b>로 자유형 방을 그리거나</div>
              <div style={{fontSize:13}}><b>+ 사각형</b>으로 방을 추가하세요</div>
            </div>
          )}
          <svg ref={svgRef} width={svgW} height={svgH}
            onMouseMove={onSVGMouseMove} onMouseUp={onSVGMouseUp} onMouseLeave={onSVGMouseUp}
            onClick={onSVGClick} onDoubleClick={onSVGDblClick}
            style={{display:'block'}}>
            {/* 격자 */}
            <defs>
              <pattern id="g10" width={SNAP*scale} height={SNAP*scale} patternUnits="userSpaceOnUse">
                <path d={`M ${SNAP*scale} 0 L 0 0 0 ${SNAP*scale}`} fill="none" stroke="#dde" strokeWidth={.5}/>
              </pattern>
              <pattern id="g100" width={100*scale} height={100*scale} patternUnits="userSpaceOnUse">
                <path d={`M ${100*scale} 0 L 0 0 0 ${100*scale}`} fill="none" stroke="#bbc" strokeWidth={1}/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="#eef0f3"/>
            <rect width="100%" height="100%" fill="url(#g10)"/>
            <rect width="100%" height="100%" fill="url(#g100)"/>

            {/* 방들 */}
            {rooms.map(room=>(
              <RoomPolygon key={room.id}
                room={room} scale={scale} bbMinX={bb.minX} bbMinY={bb.minY}
                selected={selId===room.id} showVertices={selId===room.id}
                onMouseDown={e=>mode==='select'?onRoomMouseDown(e,room):undefined}
                onVertexMouseDown={(e,vi)=>onVertexMouseDown(e,room,vi)}/>
            ))}

            {/* 가구들 */}
            {items.map(it=>{
              const ix=(it.x-bb.minX)*scale, iy=(it.y-bb.minY)*scale;
              const iw=it.width*scale, ih=it.height*scale;
              const sel=selId===it.id;
              return (
                <g key={it.id} transform={`translate(${ix},${iy}) rotate(${it.rotation||0},${iw/2},${ih/2})`}
                  onMouseDown={e=>onItemMouseDown(e,it)} style={{cursor:'pointer',userSelect:'none'}}>
                  <FurnitureShape type={it.type} w={iw} h={ih} color={it.color} selected={sel}/>
                  {sel&&<rect x={-2} y={-2} width={iw+4} height={ih+4} fill="none" stroke="#1d6ef5" strokeWidth={1.5} strokeDasharray="5,3"/>}
                  {it.label&&<text x={iw/2} y={ih+11} textAnchor="middle" fontSize={9} fill="#555" style={{pointerEvents:'none'}}>{it.label}</text>}
                </g>
              );
            })}

            {/* 폴리곤 그리기 중 프리뷰 */}
            {mode==='drawPoly' && drawPts.length>0 && hoverPt && (
              <g style={{pointerEvents:'none'}}>
                {drawPts.map((p,i)=>{
                  const x=(p.x-bb.minX)*scale, y=(p.y-bb.minY)*scale;
                  const nx=i+1<drawPts.length?(drawPts[i+1].x-bb.minX)*scale:(hoverPt.x-bb.minX)*scale;
                  const ny=i+1<drawPts.length?(drawPts[i+1].y-bb.minY)*scale:(hoverPt.y-bb.minY)*scale;
                  return <line key={i} x1={x} y1={y} x2={nx} y2={ny} stroke="#1d6ef5" strokeWidth={2} strokeDasharray="6,3"/>;
                })}
                {/* 현재 마우스 → 첫 점 (닫기 미리보기) */}
                {drawPts.length>=3&&(()=>{
                  const fp=drawPts[0]; const fpX=(fp.x-bb.minX)*scale, fpY=(fp.y-bb.minY)*scale;
                  const hx=(hoverPt.x-bb.minX)*scale, hy=(hoverPt.y-bb.minY)*scale;
                  const close=Math.hypot(hx-fpX,hy-fpY)<CLOSE_D;
                  return <line x1={hx} y1={hy} x2={fpX} y2={fpY} stroke={close?'#FF6F0F':'#1d6ef5'} strokeWidth={1.5} strokeDasharray="4,3" opacity={.6}/>;
                })()}
                {/* 꼭짓점 점들 */}
                {drawPts.map((p,i)=>{
                  const x=(p.x-bb.minX)*scale, y=(p.y-bb.minY)*scale;
                  const isFirst=i===0 && drawPts.length>=3;
                  const fpX=(drawPts[0].x-bb.minX)*scale, fpY=(drawPts[0].y-bb.minY)*scale;
                  const hx=(hoverPt.x-bb.minX)*scale, hy=(hoverPt.y-bb.minY)*scale;
                  const near=isFirst&&Math.hypot(hx-fpX,hy-fpY)<CLOSE_D;
                  return <circle key={i} cx={x} cy={y} r={near?10:5} fill={near?'#FF6F0F':'#1d6ef5'} stroke="#fff" strokeWidth={1.5}/>;
                })}
                {/* 마우스 포인터 점 */}
                <circle cx={(hoverPt.x-bb.minX)*scale} cy={(hoverPt.y-bb.minY)*scale} r={4} fill="#1d6ef5" opacity={.7}/>
              </g>
            )}

            {/* 구획 나누기 프리뷰 */}
            {mode==='partition' && partSt && hoverPt && (()=>{
              const sp=partSt.edgePt;
              const x1=(sp.x-bb.minX)*scale, y1=(sp.y-bb.minY)*scale;
              const x2=(hoverPt.x-bb.minX)*scale, y2=(hoverPt.y-bb.minY)*scale;
              return (
                <g style={{pointerEvents:'none'}}>
                  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#EA580C" strokeWidth={2} strokeDasharray="8,4"/>
                  <circle cx={x1} cy={y1} r={6} fill="#EA580C" stroke="#fff" strokeWidth={1.5}/>
                </g>
              );
            })()}
          </svg>
        </div>

        {/* ── 우측 속성 패널 ──────────────────────────────────────────────── */}
        <div style={{width:240,background:'#fff',borderLeft:'1px solid #e8e8e8',display:'flex',flexDirection:'column',flexShrink:0,overflow:'auto'}}>
          <div style={{padding:'10px 12px',borderBottom:'1px solid #f0f0f0',fontWeight:700,fontSize:12,color:'#333'}}>⚙️ 속성</div>
          {!selId&&<div style={{padding:14,fontSize:12,color:'#bbb',textAlign:'center',paddingTop:30}}>요소를 클릭해<br/>속성을 편집하세요</div>}

          {/* 방 속성 */}
          {selectedRoom&&(
            <div style={{padding:'10px 12px',flex:1,overflow:'auto'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:13}}>🏠 {selectedRoom.name}</div>
                <button onClick={()=>deleteRoom(selectedRoom.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#f44',fontSize:16}}>🗑</button>
              </div>
              <div style={{background:'#f0f5ff',borderRadius:8,padding:'8px 10px',marginBottom:10}}>
                <div style={{fontSize:16,fontWeight:800,color:'#1d6ef5'}}>{polyArea(selectedRoom.points).toFixed(2)}m²</div>
                <div style={{fontSize:11,color:'#888'}}>{(polyArea(selectedRoom.points)/3.306).toFixed(1)}평 · {selectedRoom.points.length}각형</div>
              </div>
              {/* 방 이름 */}
              <div style={{marginBottom:8}}>
                <div style={{fontSize:10,color:'#888',marginBottom:3}}>방 이름</div>
                <input value={selectedRoom.name}
                  onChange={e=>updateRoom(selectedRoom.id,{name:e.target.value})}
                  style={{width:'100%',padding:'6px 8px',border:'1px solid #ddd',borderRadius:6,fontSize:12,boxSizing:'border-box'}}/>
              </div>
              {/* 바닥 색상 */}
              <div style={{marginBottom:10}}>
                <div style={{fontSize:10,color:'#888',marginBottom:4}}>바닥 색상</div>
                <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                  {floorColors.map(c=>(
                    <div key={c} onClick={()=>updateRoom(selectedRoom.id,{floorColor:c})}
                      style={{width:22,height:22,borderRadius:4,background:c,cursor:'pointer',border:selectedRoom.floorColor===c?'2px solid #1d6ef5':'1px solid #ddd'}}/>
                  ))}
                  <input type="color" value={selectedRoom.floorColor||'#fdf8f0'}
                    onChange={e=>updateRoom(selectedRoom.id,{floorColor:e.target.value})}
                    style={{width:22,height:22,border:'1px solid #ddd',borderRadius:4,cursor:'pointer',padding:0}}/>
                </div>
              </div>
              {/* 꼭짓점 좌표 편집 */}
              <div style={{marginBottom:10}}>
                <div style={{fontSize:10,color:'#888',marginBottom:4}}>꼭짓점 좌표 (드래그로 수정 가능)</div>
                {selectedRoom.points.map((p,i)=>(
                  <div key={i} style={{display:'flex',gap:4,marginBottom:3,alignItems:'center'}}>
                    <span style={{fontSize:9,color:'#aaa',minWidth:16}}>V{i+1}</span>
                    <input type="number" value={p.x} onChange={e=>{
                      const pts=[...selectedRoom.points.map(pt=>({...pt}))];
                      pts[i]={...pts[i],x:parseInt(e.target.value)||0};
                      updateRoom(selectedRoom.id,{points:pts});
                    }} style={{width:55,padding:'3px 5px',border:'1px solid #ddd',borderRadius:4,fontSize:11}} placeholder="x"/>
                    <input type="number" value={p.y} onChange={e=>{
                      const pts=[...selectedRoom.points.map(pt=>({...pt}))];
                      pts[i]={...pts[i],y:parseInt(e.target.value)||0};
                      updateRoom(selectedRoom.id,{points:pts});
                    }} style={{width:55,padding:'3px 5px',border:'1px solid #ddd',borderRadius:4,fontSize:11}} placeholder="y"/>
                    {selectedRoom.points.length>3&&(
                      <button onClick={()=>{
                        const pts=selectedRoom.points.filter((_,j)=>j!==i);
                        updateRoom(selectedRoom.id,{points:pts});
                      }} style={{background:'none',border:'none',cursor:'pointer',color:'#f44',fontSize:13,padding:0}}>×</button>
                    )}
                  </div>
                ))}
                <button onClick={()=>{
                  const pts=[...selectedRoom.points];
                  const last=pts[pts.length-1];
                  pts.push({x:last.x+SNAP,y:last.y});
                  updateRoom(selectedRoom.id,{points:pts});
                }} style={{width:'100%',padding:'4px',borderRadius:5,border:'1px dashed #ddd',background:'#f8f8f8',cursor:'pointer',fontSize:10,color:'#888',marginTop:3}}>
                  + 꼭짓점 추가
                </button>
              </div>
              {/* 문/창 편집 버튼 */}
              <button onClick={()=>setDetail(selectedRoom)}
                style={{width:'100%',padding:'8px',borderRadius:8,border:'1px solid #e8e8e8',background:'#fff',cursor:'pointer',fontSize:12,fontWeight:600,marginBottom:6}}>
                🚪 문/창문 편집
              </button>
              <button onClick={()=>{ setMode('partition'); setPartSt(null); }}
                style={{width:'100%',padding:'8px',borderRadius:8,border:'1px solid #EA580C',background:'#fffbe6',cursor:'pointer',fontSize:12,fontWeight:600,color:'#52525B'}}>
                ✂️ 이 방 구획 나누기
              </button>
            </div>
          )}

          {/* 가구 속성 */}
          {selectedItem&&(
            <div style={{padding:'10px 12px',flex:1,overflow:'auto'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:13}}>{selectedItem.label||selectedItem.type}</div>
                <button onClick={()=>deleteItem(selectedItem.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#f44',fontSize:16}}>🗑</button>
              </div>
              {[['가로(cm)','width'],['세로(cm)','height']].map(([lbl,key])=>(
                <div key={key} style={{marginBottom:7}}>
                  <div style={{fontSize:10,color:'#888',marginBottom:3}}>{lbl}</div>
                  <input type="number" value={selectedItem[key]} min={10} max={2000}
                    onChange={e=>updateItem(selectedItem.id,{[key]:parseInt(e.target.value)||selectedItem[key]})}
                    style={{width:'100%',padding:'5px 7px',border:'1px solid #ddd',borderRadius:6,fontSize:12,boxSizing:'border-box'}}/>
                </div>
              ))}
              <div style={{marginBottom:8}}>
                <div style={{fontSize:10,color:'#888',marginBottom:3}}>회전</div>
                <div style={{display:'flex',gap:3}}>
                  {[0,90,180,270].map(r=>(
                    <button key={r} onClick={()=>updateItem(selectedItem.id,{rotation:r})}
                      style={{flex:1,padding:'4px',border:'1px solid #ddd',borderRadius:4,fontSize:10,cursor:'pointer',
                        background:selectedItem.rotation===r?'#1d6ef5':'#fff',color:selectedItem.rotation===r?'#fff':'#555'}}>
                      {r}°
                    </button>
                  ))}
                </div>
              </div>
              <div style={{marginBottom:8}}>
                <div style={{fontSize:10,color:'#888',marginBottom:4}}>색상</div>
                <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                  {['#e8dcc8','#c8a060','#7a5c2e','#4a90d9','#aaa','#ddd','#555'].map(c=>(
                    <div key={c} onClick={()=>updateItem(selectedItem.id,{color:c})}
                      style={{width:20,height:20,borderRadius:3,background:c,cursor:'pointer',border:selectedItem.color===c?'2px solid #1d6ef5':'1px solid #ddd'}}/>
                  ))}
                  <input type="color" value={selectedItem.color||'#e8dcc8'}
                    onChange={e=>updateItem(selectedItem.id,{color:e.target.value})}
                    style={{width:20,height:20,border:'1px solid #ddd',borderRadius:3,cursor:'pointer',padding:0}}/>
                </div>
              </div>
              <button onClick={()=>duplicateItem(selectedItem.id)}
                style={{width:'100%',padding:'7px',borderRadius:7,border:'1px solid #ddd',background:'#f8f8f8',cursor:'pointer',fontSize:11,fontWeight:600}}>
                📋 복사
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── 문/창 모달 ──────────────────────────────────────────────────────── */}
      {detailRoom&&<RoomDetailModal room={detailRoom}
        onUpdate={r=>{ updateRoom(r.id,r); setDetail(null); }}
        onClose={()=>setDetail(null)}/>}

      {/* ── 템플릿 모달 ──────────────────────────────────────────────────────── */}
      {showTpl&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}
             onClick={e=>{if(e.target===e.currentTarget)setShowTpl(false);}}>
          <div style={{background:'#fff',borderRadius:14,padding:24,width:560,maxWidth:'95vw',maxHeight:'85vh',overflow:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.2)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
              <div style={{fontWeight:800,fontSize:17}}>📋 템플릿으로 시작하기</div>
              <button onClick={()=>setShowTpl(false)} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#888'}}>×</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10}}>
              {TEMPLATES.map(tpl=>(
                <div key={tpl.id}
                  onClick={()=>{if(rooms.length===0||window.confirm(`"${tpl.name}" 템플릿 적용?\n현재 도면이 교체됩니다.`))applyTemplate(tpl);}}
                  style={{border:'1px solid #e8e8e8',borderRadius:10,padding:14,cursor:'pointer',textAlign:'center',background:'#fafafa',transition:'all .12s'}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='#1d6ef5';e.currentTarget.style.background='#f0f5ff';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='#e8e8e8';e.currentTarget.style.background='#fafafa';}}>
                  <div style={{fontSize:32,marginBottom:6}}>{tpl.emoji}</div>
                  <div style={{fontWeight:700,fontSize:13,color:'#333'}}>{tpl.name}</div>
                  <div style={{fontSize:10,color:'#999',marginTop:3}}>방 {tpl.rooms.length}개 · 가구 {tpl.items.length}개</div>
                </div>
              ))}
              <div onClick={()=>{commit([],[]);setShowTpl(false);}}
                style={{border:'1px dashed #ddd',borderRadius:10,padding:14,cursor:'pointer',textAlign:'center',background:'#fafafa'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor='#999'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='#ddd'}>
                <div style={{fontSize:32,marginBottom:6}}>⬜</div>
                <div style={{fontWeight:700,fontSize:13,color:'#888'}}>빈 도면</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
